/**
 * Line Item Management JavaScript
 * Handles real-time calculations, drag-and-drop, and HTMX interactions
 */

import { formatCurrency, parseCurrency, getLangPrefix, gettext as _ } from './utils.js';
import Sortable from 'sortablejs';

/**
 * Initialize line item management
 */
export function init() {
    initializeCalculations();
    initializeHTMXHandlers();
    recalculateAllGroupSubtotals();
    initializeMobileFeatures();
    initializeDragAndDrop();

    // Make group subtotal recalculation available globally for HTMX calls
    window.recalculateAllGroupSubtotals = recalculateAllGroupSubtotals;
}

/**
 * Real-time calculations for line items and quote totals
 */
function initializeCalculations() {
    // Calculate line item subtotals on quantity/unit cost changes
    document.addEventListener('input', function(e) {
        if (e.target.matches('input[name="quantity"], input[name="unit_cost"], input[name="unit_price"]')) {
            updateLineItemSubtotal(e.target);
        }
    });
    // No totals request per keystroke: nothing is saved while typing, and
    // saved changes already refresh totals from their responses.
}

function updateLineItemSubtotal(element) {
    const form = element.closest('form') || element.closest('.line-item-row') || element.closest('.inline-edit-form');
    if (!form) return;

    const quantity = parseFloat(form.querySelector('input[name="quantity"]')?.value) || 0;
    const unitCost = parseFloat(form.querySelector('input[name="unit_cost"]')?.value) ||
                     parseFloat(form.querySelector('input[name="unit_price"]')?.value) || 0;
    const subtotal = quantity * unitCost;

    // Update subtotal display if element exists
    const subtotalElement = form.querySelector('.line-item-subtotal');
    if (subtotalElement) {
        subtotalElement.textContent = formatCurrency(subtotal);
    }

    // Update preview subtotal in add form
    const previewSubtotal = form.querySelector('#add-form-subtotal');
    if (previewSubtotal) {
        previewSubtotal.textContent = formatCurrency(subtotal);
    }
    
    // Update group subtotal if this item belongs to a group
    updateGroupSubtotal(element);
}

/**
 * Update line item subtotal from displayed values in a row (for inline editing)
 */
function updateLineItemSubtotalFromRow(lineItemRow) {
    if (!lineItemRow) return;

    // Extract quantity and unit cost/price from the displayed values
    const quantityElement = lineItemRow.querySelector('[id*="-quantity"]');
    const unitCostElement = lineItemRow.querySelector('[id*="-unit-cost"], [id*="-unit-price"]');
    const subtotalElement = lineItemRow.querySelector('.line-item-subtotal');

    if (!quantityElement || !unitCostElement || !subtotalElement) return;

    // Parse the displayed values (locale-aware: lv-LV uses comma decimals)
    const quantity = parseCurrency(quantityElement.textContent);
    const unitCost = parseCurrency(unitCostElement.textContent);
    const subtotal = quantity * unitCost;

    // Update the subtotal display
    subtotalElement.textContent = formatCurrency(subtotal);
}

/**
 * Update group subtotal when a line item changes
 */
function updateGroupSubtotal(element) {
    const lineItemRow = element.closest('.line-item-row');
    if (!lineItemRow) return;

    // Find the group container - could be a group-container (from list-only) or group-items div (from unified)
    let groupContainer = lineItemRow.closest('.group-container');
    let groupItemsContainer = lineItemRow.closest('.group-items');

    let groupName;
    if (groupContainer) {
        groupName = groupContainer.dataset.groupName;
    } else if (groupItemsContainer) {
        groupName = groupItemsContainer.dataset.groupName;
    } else {
        return; // No group found
    }

    if (!groupName) return;

    // Find all line items in this group - use both possible selectors
    const groupItems = document.querySelectorAll(
        `.group-container[data-group-name="${CSS.escape(groupName)}"] .line-item-row, ` +
        `.group-items[data-group-name="${CSS.escape(groupName)}"] .line-item-row`
    );
    let groupTotal = 0;

    groupItems.forEach(item => {
        const subtotalElement = item.querySelector('.line-item-subtotal');
        if (subtotalElement) {
            // Parse the subtotal value (locale-aware: lv-LV uses comma decimals)
            const subtotal = parseCurrency(subtotalElement.textContent);
            groupTotal += subtotal;
        }
    });

    // Update the group subtotal display - try both possible selectors
    const groupSubtotalElement = document.querySelector(
        `.group-container[data-group-name="${CSS.escape(groupName)}"] .group-subtotal, ` +
        `.group-header[data-group-name="${CSS.escape(groupName)}"] .group-subtotal`
    );
    if (groupSubtotalElement) {
        groupSubtotalElement.textContent = formatCurrency(groupTotal);
    }
}

/**
 * Recalculate all group subtotals on page load or after HTMX updates
 */
function recalculateAllGroupSubtotals() {
    // Handle both template structures: group-container (list-only) and group-header (unified)
    const groupContainers = document.querySelectorAll('.group-container[data-group-name], .group-header[data-group-name]');

    groupContainers.forEach(groupContainer => {
        const groupName = groupContainer.dataset.groupName;
        if (!groupName) return;

        // Find all line items in this group - use both possible selectors
        const groupItems = document.querySelectorAll(
            `.group-container[data-group-name="${CSS.escape(groupName)}"] .line-item-row, ` +
            `.group-items[data-group-name="${CSS.escape(groupName)}"] .line-item-row`
        );
        let groupTotal = 0;

        groupItems.forEach(item => {
            const subtotalElement = item.querySelector('.line-item-subtotal');
            if (subtotalElement) {
                // Parse the subtotal value (locale-aware: lv-LV uses comma decimals)
                const subtotal = parseCurrency(subtotalElement.textContent);
                groupTotal += subtotal;
            }
        });

        // Update the group subtotal display - try both possible selectors
        const groupSubtotalElement = document.querySelector(
            `.group-container[data-group-name="${CSS.escape(groupName)}"] .group-subtotal, ` +
            `.group-header[data-group-name="${CSS.escape(groupName)}"] .group-subtotal`
        );
        if (groupSubtotalElement) {
            groupSubtotalElement.textContent = formatCurrency(groupTotal);
        }
    });
}

function updateTotals() {
    const prefix = getLangPrefix();
    // Try to update quote totals
    const quoteId = getQuoteIdFromUrl();
    // The partial's root is #quote-totals, so it goes inside the wrapper;
    // swapping it into #quote-totals nested duplicate ids.
    if (quoteId && document.getElementById('quote-totals-wrapper')) {
        htmx.ajax('GET', `${prefix}/quotes/${quoteId}/totals/`, {
            target: '#quote-totals-wrapper',
            swap: 'innerHTML'
        });
        return;
    }

    // Try to update invoice totals
    const invoiceId = getInvoiceIdFromUrl();
    if (invoiceId && document.getElementById('invoice-totals')) {
        htmx.ajax('GET', `${prefix}/invoices/${invoiceId}/totals/`, {
            target: '#invoice-totals',
            swap: 'innerHTML'
        });
        return;
    }
}

/**
 * Drag-and-drop functionality for line item reordering
 */
let sortableInstances = [];

function initializeDragAndDrop() {
    const lineItemsContainer = document.querySelector('#line-items-list');
    if (!lineItemsContainer) {
        return;
    }

    // Destroy existing sortable instances if they exist
    sortableInstances.forEach(instance => {
        if (instance) {
            instance.destroy();
        }
    });
    sortableInstances = [];

    // Drag handles are already in the HTML template

    // Find all group containers that contain line items
    const groupContainers = lineItemsContainer.querySelectorAll('.group-items');
    
    // If no groups, try to make the main container sortable
    if (groupContainers.length === 0) {
        const instance = new Sortable(lineItemsContainer, {
            draggable: '.line-item-row',
            handle: '.drag-handle',
            ghostClass: 'sortable-ghost',
            chosenClass: 'sortable-chosen',
            dragClass: 'sortable-drag',
            animation: 150,
            onEnd: function(evt) {
                updateLineItemOrder();
            }
        });
        sortableInstances.push(instance);
    } else {
        // Make each group container sortable
        groupContainers.forEach(container => {
            const instance = new Sortable(container, {
                draggable: '.line-item-row',
                handle: '.drag-handle',
                ghostClass: 'sortable-ghost',
                chosenClass: 'sortable-chosen',
                dragClass: 'sortable-drag',
                animation: 150,
                group: 'line-items', // Allow dragging between groups
                onStart: function(evt) {
                    // Store original group info for group transfer detection
                    const lineItem = evt.item;
                    if (lineItem) {
                        const groupContainer = evt.from;
                        const originalGroup = groupContainer.dataset.groupName;
                        lineItem.dataset.originalGroup = originalGroup || '';
                    }
                },
                onEnd: function(evt) {
                    // Check for group transfer first, before reordering
                    const lineItem = evt.item;
                    const newGroupContainer = evt.to;
                    const originalGroup = lineItem.dataset.originalGroup;
                    const newGroup = newGroupContainer.dataset.groupName || '';
                    
                    if (originalGroup !== newGroup) {
                        // Item moved to a different group
                        const itemId = lineItem.dataset.itemId;
                        
                        if (window.updateLineItemGroup && itemId) {
                            // Update group - this will refresh the entire line items container
                            // so we don't need to call updateLineItemOrder. Send the target
                            // group's order so the item stays where it was dropped.
                            const orderedIds = Array.from(
                                newGroupContainer.querySelectorAll('.line-item-row[id^="line-item-"]')
                            ).map(row => row.id.replace('line-item-', ''));
                            window.updateLineItemGroup(itemId, newGroup, orderedIds);
                        } else {
                            // Fallback to regular reorder
                            updateLineItemOrder();
                        }
                    } else {
                        // No group change, just reorder within the same group
                        updateLineItemOrder();
                    }
                    
                    // Clean up temporary data
                    delete lineItem.dataset.originalGroup;
                }
            });
            sortableInstances.push(instance);
        });
    }
    
}

// addDragHandles function removed - drag handles are already in HTML template

// Touch drag is now handled by SortableJS natively

function updateLineItemOrder() {
    const listContainer = document.getElementById('line-items-list');
    if (!listContainer) {
        return;
    }

    const lineItems = listContainer.querySelectorAll('.line-item-row[id^="line-item-"]');
    if (!lineItems.length) {
        return;
    }

    const lineItemIds = Array.from(lineItems).map(item => item.id.replace('line-item-', ''));

    // Get reorder URL from data attribute
    let reorderUrl = listContainer.dataset.reorderUrl;

    // Fallback to legacy container if needed
    if (!reorderUrl) {
        const legacyContainer = document.getElementById('line-items-container');
        reorderUrl = legacyContainer?.dataset.reorderUrl;
    }
    if (!reorderUrl) {
        return;
    }

    // Get CSRF token
    const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
    if (!csrfToken) {
        return;
    }

    const payload = {
        csrfmiddlewaretoken: csrfToken,
        // Ensure the order is preserved by sending as comma-separated string
        'line_item_ids[]': lineItemIds
    };

    try {
        htmx.ajax('POST', reorderUrl, {
            values: payload,
            target: '#line-items-list',
            swap: 'innerHTML'
        });
    } catch (error) {
        showError(_('Failed to reorder items. Please try again.'));
    }
}

/**
 * HTMX event handlers and enhancements
 */
function initializeHTMXHandlers() {
    // Re-initialize features after any HTMX content is updated
    document.addEventListener('htmx:afterSwap', function(evt) {
        if (evt.detail.target.id === 'line-items-list') {
            // Update totals for both quotes and invoices
            updateTotals();
            // Recalculate group subtotals after HTMX updates
            recalculateAllGroupSubtotals();
            // Re-initialize drag and drop for new/updated content
            // Small delay to ensure DOM is fully updated
            setTimeout(() => {
                initializeDragAndDrop();
            }, 100);
        }

        // Also handle inline editing updates (these target individual elements)
        const targetElement = evt.detail.target;
        if (targetElement && targetElement.closest('.line-item-row')) {
            // This was an inline edit - recalculate subtotals
            const lineItemRow = targetElement.closest('.line-item-row');

            // Update the specific line item subtotal
            updateLineItemSubtotalFromRow(lineItemRow);

            // Update group subtotal if this item belongs to a group
            updateGroupSubtotal(targetElement);

            // Update overall totals
            updateTotals();
        }

        // Mobile features are now re-applied globally via main.js HTMX handlers
    });

    // Show loading states, but only on this module's own targets.
    //
    // These listeners are registered on `document`, so they see every htmx
    // request in the app. Applying the line-item loading style to whatever
    // element happened to be the swap target put a line-item spinner on
    // unrelated containers: creating a customer from the project form left
    // #modal-container with `.loading`, whose ::after is a 20px spinning ring
    // centred in the element. Because that container is an empty, zero-height
    // div below the form, the ring appeared to float under the card with
    // nothing to attach it to, and `.loading`'s pointer-events: none plus its
    // pseudo-element nature made it unselectable in the DOM inspector.
    document.addEventListener('htmx:beforeRequest', function(evt) {
        if (isLineItemTarget(evt.detail.target)) {
            showLoadingState(evt.detail.target);
        }
    });

    document.addEventListener('htmx:afterRequest', function(evt) {
        if (isLineItemTarget(evt.detail.target)) {
            hideLoadingState(evt.detail.target);
        }
    });

    // Handle validation errors for line item management
    document.addEventListener('htmx:responseError', function(evt) {
        // Only handle line item related errors, let others be handled by centralized handler
        const target = evt.detail.target;
        if (target && (target.id === 'line-items-list' || target.closest('#line-items-list'))) {
            const status = evt.detail && evt.detail.xhr ? evt.detail.xhr.status : null;
            if (status === 403) {
                showError(_('Access denied. You do not have permission to perform this action.'));
            } else {
                showError(_('An error occurred. Please try again.'));
            }
            evt.detail.handled = true;
        }
    });
}

/**
 * Mobile-specific features and optimizations
 */
function initializeMobileFeatures() {
    // Optimize keyboard input for numeric fields
    const numericFields = document.querySelectorAll('input[type="number"]');
    numericFields.forEach(field => {
        field.setAttribute('inputmode', 'decimal');
        // Removed automatic scrolling on focus to prevent unwanted scroll behavior
    });

    // Mobile features now handled globally in main.js
    // Group collapse is handled once, by delegation, in group_management.js.
}

/**
 * Utility functions
 */
function getQuoteIdFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/\/quotes\/(\d+)\//);
    return match ? match[1] : null;
}

function getInvoiceIdFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/\/invoices\/(\d+)\//);
    return match ? match[1] : null;
}

// Expose getLangPrefix globally for inline scripts in templates
window.getLangPrefix = getLangPrefix;

/**
 * Whether an htmx swap target belongs to line-item editing.
 *
 * The loading style below is a line-item visual, so it must only ever be
 * applied to line-item targets. Anything else in the app that swaps content
 * (modals, dropdowns, dashboard cards) has its own loading treatment.
 *
 * @param {Element} element - the htmx swap target
 * @returns {boolean}
 */
function isLineItemTarget(element) {
    if (!element || !element.closest) {
        return false;
    }
    return Boolean(element.closest(
        '#line-items-list, #line-items-container, #groups-container, ' +
        '.line-item-row, .group-container, .group-items, .inline-edit-form'
    ));
}

function showLoadingState(element) {
    element.classList.add('loading');
    element.style.opacity = '0.7';
}

function hideLoadingState(element) {
    element.classList.remove('loading');
    element.style.opacity = '1';
}

function showError(message) {
    // Create or update error message
    let errorDiv = document.getElementById('error-message');
    if (!errorDiv) {
        errorDiv = document.createElement('div');
        errorDiv.id = 'error-message';
        errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg z-50';
        document.body.appendChild(errorDiv);
    }
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
        errorDiv.style.display = 'none';
    }, 5000);
}

// No longer needed - form is always visible
// function hideAddLineItemForm() {
//     const form = document.getElementById('add-line-item-form');
//     if (form) {
//         form.classList.add('hidden');
//     }
// }

/**
 * Toggle add line item form visibility
 */
export function toggleAddLineItemForm() {
    const form = document.getElementById('add-line-item-form');
    if (form) {
        form.classList.toggle('hidden');
        
        // Focus first input when showing form
        if (!form.classList.contains('hidden')) {
            const firstInput = form.querySelector('input, select, textarea');
            if (firstInput) {
                firstInput.focus();
            }
        }
    }
}

